import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { keyframes } from "@mui/system";
import { motion } from "framer-motion";

// Keyframes for pulse animation
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(14, 144, 70, 0.7);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(14, 144, 70, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(14, 144, 70, 0);
  }
`;

// Keyframes for gradient animation
const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const AuthView = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay to allow animation to play after component mounts
    setTimeout(() => setVisible(true), 100);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        padding: "20px",
        backgroundColor: isDark ? "#000000" : "transparent",
      }}
    >
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ 
          opacity: visible ? 1 : 0, 
          y: visible ? 0 : 20,
          transition: { duration: 0.5 }
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 30px",
          borderRadius: "16px",
          maxWidth: "450px",
          width: "100%",
          position: "relative",
          overflow: "hidden",
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(14, 144, 70, 0.08)",
          border: "1px solid rgba(14, 144, 70, 0.2)",
          boxShadow: "0 8px 32px rgba(14, 144, 70, 0.15)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: "linear-gradient(90deg, #0E9046, #4FC389, #0E9046)",
            backgroundSize: "200% 200%",
            animation: `${gradientAnimation} 3s ease infinite`,
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: "rgba(79, 195, 137, 0.8)",
            borderRadius: "50%",
            width: "80px",
            height: "80px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "20px",
            animation: `${pulse} 2s infinite`,
          }}
          component={motion.div}
          whileHover={{ scale: 1.05 }}
        >
          <LockIcon 
            sx={{ 
              fontSize: 40, 
              color: "#0E9046" 
            }}
            component={motion.svg}
            animate={{ 
              rotateZ: [0, 10, -10, 0],
              transition: { 
                repeat: Infinity,
                repeatDelay: 3,
                duration: 0.5
              }
            }}
          />
        </Box>

        <Typography 
          variant="h5" 
          component={motion.h5}
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: visible ? 1 : 0,
            transition: { delay: 0.3, duration: 0.5 }
          }}
          sx={{ 
            fontWeight: 600, 
            color: isDark ? "#ffffff" : "#343a40",
            marginBottom: "8px",
            textAlign: "center"
          }}
        >
          Access Restricted
      </Typography>

        <Typography 
          variant="body1" 
          component={motion.p}
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: visible ? 1 : 0,
            transition: { delay: 0.5, duration: 0.5 }
          }}
          sx={{ 
            color: isDark ? "#cccccc" : "#6c757d", 
            textAlign: "center",
            marginBottom: "24px",
            maxWidth: "320px"
          }}
        >
          You don't have the necessary permissions to access this section. Please contact your administrator for assistance.
      </Typography>
      </Box>
    </Box>
  );
};

export default AuthView;
